import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Copy, ExternalLink, IndianRupee, RefreshCw, Send } from "lucide-react";

const TONE = {
  created: "border-amber-500/50 text-amber-700 bg-amber-500/10",
  partially_paid: "border-sky-500/40 text-sky-700 bg-sky-50",
  paid: "border-emerald-500/40 text-emerald-700 bg-emerald-50",
  expired: "border-border text-muted-foreground",
  cancelled: "border-border text-muted-foreground",
};

const WORDING = {
  created: "Waiting for payment",
  partially_paid: "Part paid",
  paid: "Paid",
  expired: "Expired",
  cancelled: "Cancelled",
};

const money = (value) => `₹${Number(value || 0).toFixed(2)}`;

/**
 * A Razorpay Payment Link for one prescription request: the clinic types the agreed
 * amount, picks the channels, and the patient gets the link on their phone. Razorpay's
 * webhook flips it to Paid on its own; "Check status" reads it live in between.
 */
export const RxPayLink = ({ upload, draft, onDraft, onChanged }) => {
  const link = upload.rzp_link || null;
  const active = link && ["created", "partially_paid"].includes(link.status);
  const [form, setForm] = useState({
    amount: "",
    description: "",
    whatsapp: true,
    sms: false,
  });
  const [open, setOpen] = useState(!link);
  const [busy, setBusy] = useState(false);

  // An amount the clinic had started working out is still here when they come back.
  useEffect(() => {
    const kept = draft || {};
    setForm((f) => ({
      amount: kept.pay_amount ?? f.amount,
      whatsapp: kept.pay_whatsapp ?? f.whatsapp,
      sms: kept.pay_sms ?? f.sms,
      description: kept.pay_description || f.description ||
        `${upload.name} · prescription ${String(upload.id).slice(0, 8)}`,
    }));
    setOpen(!upload.rzp_link || Boolean(kept.pay_amount));
  }, [upload.id, upload.name, upload.rzp_link, draft]);

  const change = (patch, draftPatch) => {
    setForm((f) => ({ ...f, ...patch }));
    onDraft?.(draftPatch);
  };

  // A link the patient is still expected to pay is re-checked while the clinic watches.
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => sync(true), 20000);
    return () => clearInterval(timer);
  }, [active, upload.id]);

  const sync = async (quiet = false) => {
    try {
      const { data } = await api.post(`/admin/prescription-uploads/${upload.id}/payment-link/sync`);
      if (!quiet) toast.success(`Razorpay says: ${WORDING[data.link.status] || data.link.status}`);
      if (data.link.status !== link?.status) onChanged?.();
    } catch (e) {
      if (!quiet) toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const create = async (replace = false) => {
    const amount = Number(form.amount);
    if (!amount || amount < 1) return toast.error("Enter the amount the patient has agreed to pay");
    if (!form.whatsapp && !form.sms) return toast.error("Pick at least one channel to send the link on");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/prescription-uploads/${upload.id}/payment-link`, {
        amount,
        description: form.description,
        whatsapp: form.whatsapp,
        sms: form.sms,
        replace,
      });
      const failed = Object.entries(data.link.sent || {}).filter(([, r]) => r.status !== "sent");
      toast.success(`Link for ${money(amount)} created${failed.length ? "" : " and sent"}`);
      failed.forEach(([channel, r]) => toast.error(`${channel}: ${r.detail}`));
      setForm((f) => ({ ...f, amount: "" }));
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard?.writeText(link.short_url);
    toast.success("Link copied");
  };

  return (
    <div className="rounded-xl border border-border p-4" data-testid="rx-paylink">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h4 className="font-display text-xl flex items-center gap-2"><IndianRupee size={15} /> Payment link</h4>
        {link && (
          <Badge variant="outline" className={TONE[link.status] || ""} data-testid="rx-paylink-status">
            {WORDING[link.status] || link.status}
          </Badge>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Razorpay collects the money and marks this request paid by itself — no order or login needed.
      </p>

      {link && (
        <div className="space-y-2 mb-4 text-sm" data-testid="rx-paylink-current">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium" data-testid="rx-paylink-amount">{money(link.amount)}</span>
            {link.amount_paid > 0 && <span className="text-xs text-muted-foreground">{money(link.amount_paid)} received</span>}
            <span className="text-xs text-muted-foreground truncate">{link.description}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="text-xs bg-muted/60 rounded-lg px-2 py-1 truncate max-w-[260px]" data-testid="rx-paylink-url">
              {link.short_url}
            </code>
            <Button size="sm" variant="outline" className="btn-pill h-8" onClick={copy} data-testid="rx-paylink-copy">
              <Copy size={12} className="mr-1.5" /> Copy
            </Button>
            <a href={link.short_url} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" className="btn-pill h-8" data-testid="rx-paylink-open">
                <ExternalLink size={12} className="mr-1.5" /> Open
              </Button>
            </a>
            <Button size="sm" variant="ghost" className="btn-pill h-8" onClick={() => sync(false)}
                    data-testid="rx-paylink-sync">
              <RefreshCw size={12} className="mr-1.5" /> Check status
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground" data-testid="rx-paylink-sent">
            {Object.entries(link.sent || {}).map(([channel, result]) => (
              <Badge key={channel} variant="outline" className="capitalize">
                {channel}: {result.status}
              </Badge>
            ))}
            {link.expires_at && link.status === "created" && (
              <span>Expires {new Date(link.expires_at).toLocaleString("en-IN")}</span>
            )}
          </div>
        </div>
      )}

      {!open ? (
        <Button size="sm" variant="outline" className="btn-pill h-9" onClick={() => setOpen(true)}
                data-testid="rx-paylink-new">
          {active ? "Cancel and send a new link" : "Create a new link"}
        </Button>
      ) : (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="overline">Amount (₹)</label>
              <Input
                value={form.amount}
                onChange={(e) => {
                  const amount = e.target.value.replace(/[^\d.]/g, "");
                  change({ amount }, { pay_amount: amount });
                }}
                placeholder="1250"
                inputMode="decimal"
                className="mt-2 bg-transparent"
                data-testid="rx-paylink-amount-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="overline">Description (optional)</label>
              <Input
                value={form.description}
                onChange={(e) => change({ description: e.target.value },
                                        { pay_description: e.target.value })}
                className="mt-2 bg-transparent"
                data-testid="rx-paylink-description"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={form.whatsapp}
                onCheckedChange={(v) => change({ whatsapp: !!v }, { pay_whatsapp: !!v })}
                data-testid="rx-paylink-wa"
              />
              Send on WhatsApp
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={form.sms}
                onCheckedChange={(v) => change({ sms: !!v }, { pay_sms: !!v })}
                data-testid="rx-paylink-sms"
              />
              Send by SMS
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy} className="btn-action h-10" onClick={() => create(!!active)}
                    data-testid="rx-paylink-create">
              <Send size={14} className="mr-1.5" />
              {busy ? "Creating…" : active ? "Cancel old link & send new" : "Create & send link"}
            </Button>
            {link && (
              <Button variant="ghost" className="btn-pill h-10" onClick={() => setOpen(false)}
                      data-testid="rx-paylink-cancel-form">
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RxPayLink;
